import {
	ChangeDetectionStrategy,
	Component,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Address } from '@models/address';
import { MatterTitle } from '@models/matter';
import {
	CloserSyncInstruction,
	CloserSyncTransaction,
} from '@models/mortgages/closer-sync-instruction';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import {
	CloserSyncRepoService,
	UpdateFields,
} from '@repos/mortgages/closer-sync/closer-sync.service';
import { CloserSyncInstructionRepoService } from '@repos/mortgages/repos/closerSyncInstructionRepo';
import { CloserSyncTransactionRepoService } from '@repos/mortgages/repos/closerSyncTransactionRepo';
import { NavigationService } from '@services/nav/nav.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import LogRocket from 'logrocket';
import { BehaviorSubject, Observable } from 'rxjs';
import { parseInboundInstructionDto } from '@repos/mortgages/closer-sync/utils';

enum State {
	SYNC = 'sync',
	REVIEW = 'review',
}

@UntilDestroy()
@Component({
	selector: 'app-closer-sync-create-mortgage',
	templateUrl: './closer-sync-create-mortgage.component.html',
	styleUrls: ['./closer-sync-create-mortgage.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CloserSyncCreateMortgageComponent implements OnInit, OnDestroy {
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(MatterTitlesState.getPrimaryTitle) primaryTitle: MatterTitle;

	public states = State;
	public state = State.REVIEW;

	private mergeFields: UpdateFields;

	private savingSubject = new BehaviorSubject<boolean>(false);
	private transactionSubject = new BehaviorSubject<CloserSyncTransaction>(null);
	private instructionSubject = new BehaviorSubject<CloserSyncInstruction>(null);
	private loadingSubject = new BehaviorSubject<boolean>(true);
	public loading$: Observable<boolean> = this.loadingSubject.asObservable();
	public instruction$: Observable<CloserSyncInstruction> =
		this.instructionSubject.asObservable();
	public transaction$: Observable<CloserSyncTransaction> =
		this.transactionSubject.asObservable();
	public saving$: Observable<boolean> = this.savingSubject.asObservable();
	public mortgageeServiceAddress: Address;
	public mortgageeReportAddress: Address;
	public newMatter: boolean;

	constructor(
		private route: ActivatedRoute,
		private transactionRepo: CloserSyncTransactionRepoService,
		private instructionsRepo: CloserSyncInstructionRepoService,
		private closerSyncRepoService: CloserSyncRepoService,
		private navigationService: NavigationService,
		private snackbarService: SnackbarService,
	) {}
	ngOnDestroy(): void {
		this.loadingSubject.unsubscribe();
		this.instructionSubject.unsubscribe();
		this.transactionSubject.unsubscribe();
		this.savingSubject.unsubscribe();
	}

	async ngOnInit(): Promise<void> {
		this.loadingSubject.next(true);

		this.route?.queryParams
			.pipe(untilDestroyed(this))
			.subscribe(async (params) => {
				this.newMatter = params.type === 'newMatter';
			});

		this.route?.params.pipe(untilDestroyed(this)).subscribe(async (params) => {
			try {
				const instructionId = params.transactionId;
				const transactionDoc = await this.transactionRepo
					.Get(instructionId)
					.toPromise();
				const latestInstructionId = transactionDoc.data().latestVersionId;
				const instructionDocData = await this.instructionsRepo
					.Get(latestInstructionId, { transactionId: instructionId })
					.toPromise();
				const instructionDoc = parseInboundInstructionDto(
					instructionDocData.data() as CloserSyncInstruction,
				);

				this.transactionSubject.next(
					transactionDoc.data() as CloserSyncTransaction,
				);
				this.instructionSubject.next(instructionDoc);

				this.loadingSubject.next(false);
			} catch (err) {
				this.loadingSubject.next(false);
				this.snackbarService.openSnackBar({
					text: 'Unable to load mortgage. Please refresh.',
					type: 'error',
				});
				console.log('Error loading transaction', err);
			}
		});
	}

	async createMortgage() {
		try {
			this.savingSubject.next(true);
			const result = await this.closerSyncRepoService.assignMatterToMortgage(
				this.transactionSubject.value,
				this.matterId,
				this.mergeFields,
				this.primaryTitle?.id,
			);
			if (!result) {
				this.snackbarService.openSnackBar({
					text: 'Unable to create mortgage. Please refresh.',
					type: 'error',
				});
				throw new Error('Unable to create mortgage');
			}
			this.navigationService.goToMatter_Mortgages_Mortgage(
				this.matterId,
				result.matterMortgageId,
			);
		} catch (err) {
			LogRocket.error(
				'Unable to create mortgage',
				`Error: ${err.message}`,
				err,
			);
			console.log('Error creating mortgage', err);
		} finally {
			this.savingSubject.next(false);
		}
	}

	setMergedFields(ev: UpdateFields) {
		this.mergeFields = ev;
	}
}
